import type { FunctionComponent, PropsWithChildren } from 'react';
import { useMemo } from 'react';

import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { BoundCommentsScopeProvider } from '../../../comments/comments-scope-context';
import { BoundCommentsSurfaceProvider } from '../../../comments/surface/BoundCommentsSurfaceProvider';
import { useJobItemVersions } from '../../api/use-job-item-versions';
import { createJobProfileCommentsSubject } from './job-profile-comments-adapter';
import { useJobProfileCommentsApi } from './job-profile-comments-api';

type JobProfileCommentsSubjectParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly language: string;
    readonly requestedVersion?: number;
};

export const useJobProfileCommentsSubject = ({
    architectureId,
    itemId,
    itemType,
    language,
    requestedVersion,
}: JobProfileCommentsSubjectParams) => {
    const { data: versions } = useJobItemVersions({
        architectureId,
        itemId,
        itemType,
    });
    return useMemo(
        () =>
            createJobProfileCommentsSubject({
                architectureId,
                availableVersions: versions?.map(({ version }) => version) ?? [],
                itemId,
                itemType,
                language,
                requestedVersion,
            }),
        [architectureId, itemId, itemType, language, requestedVersion, versions],
    );
};

export const JobProfileCommentsScopeProvider: FunctionComponent<PropsWithChildren<JobProfileCommentsSubjectParams>> = ({
    children,
    ...params
}) => {
    const api = useJobProfileCommentsApi();
    const subject = useJobProfileCommentsSubject(params);
    return (
        <BoundCommentsScopeProvider api={api} subject={subject}>
            {children}
        </BoundCommentsScopeProvider>
    );
};

type JobProfileCommentsSurfaceProviderProps = PropsWithChildren<{
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly language: LanguageCodeForJSA;
    readonly version?: number;
}>;

export const JobProfileCommentsSurfaceProvider: FunctionComponent<JobProfileCommentsSurfaceProviderProps> = ({
    architectureId,
    children,
    itemId,
    itemType,
    language,
    version,
}) => {
    const api = useJobProfileCommentsApi();
    const subject = useJobProfileCommentsSubject({
        architectureId,
        itemId,
        itemType,
        language,
        requestedVersion: version,
    });
    return (
        <BoundCommentsSurfaceProvider api={api} subject={subject}>
            {children}
        </BoundCommentsSurfaceProvider>
    );
};
